// Mock inbox for Inbox & Triage (ICT-2 Proxy Material Inbox Audit + ICT-4 ICT
// Inbox routing). Pure frontend fixture, mirrors demoEmails.ts's shape.
import { PROXY_SEED_TODAY } from './mockProxyJobs';

export interface ProxyInboxEmail {
  id: string;
  from: string;
  fromEmail: string;
  to: string;
  cc: string[];
  subject: string;
  date: string; // ISO date, always PROXY_SEED_TODAY for "today" audit fixtures
  body: string;
  relatedJobId?: string;
  // RDQ_ORDER-specific — only meaningful on REVISED_RDQ-classified emails.
  // Ace's own confidence in this email's extraction/classification (0-100);
  // when absent, the registry falls back to the classifier's flat rate.
  // revisedFullSets/revisedNaa are the holder-count-driven quantities the
  // solicitor is asking for, distinct from the job's on-file fullSetsNeeded/
  // naaNeeded — this is what makes the RDQ panel's "change vs order on file"
  // delta real instead of always zero.
  aceConfidence?: number;
  revisedFullSets?: number;
  revisedNaa?: number;
}

const TODAY = PROXY_SEED_TODAY;

export const PROXY_INBOX_EMAILS: ProxyInboxEmail[] = [
  {
    id: 'pinbox-001', from: 'Georgeson', fromEmail: 'proxy@georgeson.com',
    to: 'proxymaterial@betanxt.com', cc: ['documents@betanxt.com', 'verifications@betanxt.com', 'notifications@betanxt.com'],
    subject: 'Proxy Statement PDF - Powell Industries Inc (Job 3021919)',
    date: TODAY, body: 'Attached: final Proxy Statement + Proxy Card for Powell Industries Inc, Job 3021919.',
    relatedJobId: '3021919',
  },
  {
    id: 'pinbox-002', from: 'Georgeson', fromEmail: 'proxy@georgeson.com',
    to: 'proxymaterial@betanxt.com', cc: ['documents@betanxt.com'],
    subject: 'Distribution method change - Cascade Timber Holdings (Job 9100001)',
    date: TODAY, body: 'The issuer has moved this meeting to electronic distribution only. Please update the job.',
    relatedJobId: '9100001',
  },
  {
    id: 'pinbox-003', from: 'Discovery', fromEmail: 'documents@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Billing verification needed - Prairie Wind Energy Partners (Job 9100071)',
    date: TODAY, body: 'Please verify the issuer billing details for this job before it is set up for invoicing.',
    relatedJobId: '9100071',
  },
  {
    id: 'pinbox-004', from: 'Discovery', fromEmail: 'documents@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Revised RDQ needed - Granite Peak Mining (Job 9100022)',
    date: TODAY, body: 'D.F. King reports the holder counts changed since the original order. Please send a revised RDQ.',
    relatedJobId: '9100022', aceConfidence: 96, revisedFullSets: 78,
  },
  {
    id: 'pinbox-005', from: 'Discovery', fromEmail: 'documents@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'New proxy contest job - Kessler Foods International (Job 9200001)',
    date: TODAY, body: 'A proxy contest job has been set up. The dissident party, Ridgeback Capital Partners, is represented by Okapi Partners. Please send the contest outreach.',
    relatedJobId: '9200001',
  },
  {
    id: 'pinbox-006', from: 'BetaNXT Credit', fromEmail: 'credit@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Credit hold - no fulfillment (Ashford Retail Partners, Job 9100040)',
    date: TODAY, body: 'Ashford Retail Partners is on credit hold. Do not fulfill this job until the hold is lifted.',
    relatedJobId: '9100040',
  },
  {
    id: 'pinbox-007', from: 'ICT Associate', fromEmail: 'ict.associate@betanxt.com',
    to: 'ict-manager@betanxt.com', cc: ['documents@betanxt.com', 'verifications@betanxt.com', 'ict@betanxt.com'],
    subject: 'ICT Red Report 05.29.2026',
    date: '2026-05-29', body: 'Completed Red Report for 05.29.2026, with the status of each job.',
  },
  {
    id: 'pinbox-008', from: 'Kingsdale Advisors', fromEmail: 'contact@kingsdaleadvisors.com',
    to: 'proxymaterial@betanxt.com', cc: ['documents@betanxt.com'],
    subject: 'Notice/Circular PDF - Laurentian Bank of Canada (Job 3025905)',
    date: TODAY, body: 'Attached: Notice/Circular + Form of Proxy for Laurentian Bank of Canada, Job 3025905.',
    relatedJobId: '3025905',
  },
  {
    id: 'pinbox-009', from: 'Mediant Communications', fromEmail: 'proxy@mediantonline.com',
    to: 'proxymaterial@betanxt.com', cc: ['documents@betanxt.com'],
    subject: 'Job date change - Palisade Consumer Brands (Job 9100024)',
    date: TODAY, body: 'Meeting date has been rescheduled. Please update the job record.',
    relatedJobId: '9100024',
  },
  {
    id: 'pinbox-010', from: 'Verifications', fromEmail: 'verifications@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Billing verification needed - Water Corp (Job 3040869)',
    date: TODAY, body: 'Please verify the issuer billing details on this job against what was mailed.', relatedJobId: '3040869',
  },

  // ── Landed in the shared Proxy Material Inbox but missing a required CC —
  // Audit-tab exceptions (all dated today) ──
  {
    id: 'pinbox-011', from: 'Unknown Sender', fromEmail: 'random@external-vendor.com',
    to: 'proxymaterial@betanxt.com', cc: [],
    subject: 'FYI: proxy material attached',
    date: TODAY, body: 'Attaching material for review, please route as needed.',
  },
  {
    id: 'pinbox-012', from: 'Innisfree M&A Incorporated', fromEmail: 'contests@innisfreema.com',
    to: 'proxymaterial@betanxt.com', cc: [],
    subject: 'Signed contest form - Marchbanks Industrial Group (Job 9200002)',
    date: TODAY, body: 'Signed contest form attached.', relatedJobId: '9200002',
  },
  {
    id: 'pinbox-013', from: 'Kingsdale Advisors', fromEmail: 'contact@kingsdaleadvisors.com',
    to: 'proxymaterial@betanxt.com', cc: [],
    subject: 'Material status update - Job 9100060',
    date: TODAY, body: 'Update on material status, please advise.', relatedJobId: '9100060',
  },

  {
    id: 'pinbox-044', from: 'Morrow Sodali', fromEmail: 'info@investor.morrowsodali.com',
    to: 'proxymaterial@betanxt.com', cc: [],
    subject: 'Proxy Statement PDF - Sunfield Agricultural REIT (Job 9100110)',
    date: TODAY, body: 'Attached: final Proxy Statement and Proxy Card for Sunfield Agricultural REIT.', relatedJobId: '9100110',
  },
  {
    id: 'pinbox-045', from: 'Laurel Hill', fromEmail: 'assistance@laurelhill.com',
    to: 'proxymaterial@betanxt.com', cc: [],
    subject: 'Meeting date rescheduled - Hemlock Point Resorts (Job 9100091)',
    date: TODAY, body: 'The issuer has rescheduled the meeting. Please update the job record.', relatedJobId: '9100091',
  },

  // ── Yesterday — not part of today's audit window ──
  {
    id: 'pinbox-015', from: 'ICT Associate', fromEmail: 'ict.associate@betanxt.com',
    to: 'ict-manager@betanxt.com', cc: ['documents@betanxt.com', 'verifications@betanxt.com', 'ict@betanxt.com'],
    subject: 'ICT Red Report 05.28.2026',
    date: '2026-05-28', body: 'Completed Red Report for 05.28.2026, with the status of each job.',
  },

  // ── Additional REVISED_RDQ requests — each carries its own confidence and
  // revised holder-count quantities, distinct from the job's on-file order,
  // so the RDQ_ORDER workflow's "change vs order on file" delta is real. ──
  {
    id: 'pinbox-016', from: 'Discovery', fromEmail: 'documents@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Revised RDQ needed - Water Corp (Job 3040869)',
    date: TODAY, body: 'D.F. King reports more record-date holders in the Canadian intermediary book. Please recalculate Full Set + NAA quantities and send a revised RDQ.',
    relatedJobId: '3040869', aceConfidence: 93, revisedFullSets: 52, revisedNaa: 14,
  },
  {
    id: 'pinbox-017', from: 'Discovery', fromEmail: 'documents@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Revised RDQ needed - Laurentian Bank of Canada (Job 3025905)',
    date: TODAY, body: 'Kingsdale Advisors sent updated record-date figures. Please re-issue the RDQ at the revised quantities.',
    relatedJobId: '3025905', aceConfidence: 91, revisedFullSets: 34, revisedNaa: 44,
  },
  {
    id: 'pinbox-018', from: 'Discovery', fromEmail: 'documents@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Revised RDQ needed - Redstone Materials Co (Job 9100002)',
    date: TODAY, body: 'Morrow Sodali says the holder count came in lower than the original estimate. Please recalculate and reduce the order.',
    relatedJobId: '9100002', aceConfidence: 98, revisedFullSets: 60,
  },
  {
    id: 'pinbox-019', from: 'Discovery', fromEmail: 'documents@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Revised RDQ needed - Hartwell Pharmaceuticals (Job 9100020)',
    date: TODAY, body: 'Georgeson added a handful of record-date holders late. Please true up the RDQ.',
    relatedJobId: '9100020', aceConfidence: 90, revisedFullSets: 96,
  },

  // ── Everything else — realistic day-in-the-life volume across the other
  // ICT-4 routing categories, to size the Intake Queue like a real inbox. ──
  {
    id: 'pinbox-022', from: 'Mediant Communications', fromEmail: 'proxy@mediantonline.com',
    to: 'proxymaterial@betanxt.com', cc: ['documents@betanxt.com'],
    subject: 'Notice/Circular PDF - Palisade Consumer Brands (Job 9100024)',
    date: TODAY, body: 'Attached: Notice/Circular for Palisade Consumer Brands.', relatedJobId: '9100024',
  },
  {
    id: 'pinbox-023', from: 'Alliance Advisors', fromEmail: 'contact@allianceadvisors.com',
    to: 'proxymaterial@betanxt.com', cc: ['documents@betanxt.com'],
    subject: 'Form of Proxy PDF - Vantage Semiconductor (Job 9100005)',
    date: TODAY, body: 'Attached: Form of Proxy for Vantage Semiconductor.', relatedJobId: '9100005',
  },
  {
    id: 'pinbox-025', from: 'Kingsdale Advisors', fromEmail: 'contact@kingsdaleadvisors.com',
    to: 'proxymaterial@betanxt.com', cc: ['documents@betanxt.com'],
    subject: 'Meeting date rescheduled - Northwind Credit Union (Job 9100060)',
    date: TODAY, body: 'The annual meeting has been rescheduled. Please update the job record.', relatedJobId: '9100060',
  },
  {
    id: 'pinbox-027', from: 'Alliance Advisors', fromEmail: 'contact@allianceadvisors.com',
    to: 'proxymaterial@betanxt.com', cc: ['documents@betanxt.com'],
    subject: 'Job date change - Meadowlark Digital Media (Job 9100070)',
    date: TODAY, body: 'Meeting date confirmed for next quarter. Please update the record.', relatedJobId: '9100070',
  },
  {
    id: 'pinbox-028', from: 'Discovery', fromEmail: 'documents@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Billing verification needed - Sable Point Resources (Job 9100012)',
    date: TODAY, body: 'Please verify the issuer billing details on this job against what was mailed.', relatedJobId: '9100012',
  },
  {
    id: 'pinbox-029', from: 'Verifications', fromEmail: 'verifications@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Billing verification needed - Fenwick Data Systems (Job 9100023)',
    date: TODAY, body: 'Please verify the issuer billing details on this job against what was mailed.', relatedJobId: '9100023',
  },
  {
    id: 'pinbox-030', from: 'Investorcom', fromEmail: 'contests@investorcom.com',
    to: 'ict-contest@betanxt.com', cc: [],
    subject: 'Proxy contest fee - Ashcombe Retail Trust (Job 9200003)',
    date: TODAY, body: 'The dissident party is asking whether the $5,000 contest fee can be reduced to $3,500.', relatedJobId: '9200003',
  },
  {
    id: 'pinbox-031', from: 'Saratoga Proxy Consulting', fromEmail: 'intake@saratogaproxy.com',
    to: 'ict-contest@betanxt.com', cc: [],
    subject: 'Dissident contest notice - Corrigan Aerospace Dynamics (Job 9200005)',
    date: TODAY, body: 'Contest materials forwarded to Discovery. FYI only.', relatedJobId: '9200005',
  },
  {
    id: 'pinbox-032', from: 'BetaNXT Credit', fromEmail: 'credit@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Credit hold - no fulfillment (Talisman Robotics Corp, Job 9100045)',
    date: TODAY, body: 'Talisman Robotics Corp is on credit hold. Do not fulfill this job until the hold is lifted.',
    relatedJobId: '9100045',
  },
  {
    id: 'pinbox-033', from: 'BetaNXT Credit', fromEmail: 'credit@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Credit hold - no fulfillment (Wexford Municipal Bond Fund, Job 9100042)',
    date: TODAY, body: 'Wexford Municipal Bond Fund is on credit hold. Do not fulfill this job until the hold is lifted.',
    relatedJobId: '9100042',
  },
  {
    id: 'pinbox-034', from: 'Report Manager', fromEmail: 'reports@mediantonline.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Report: Proxy Material Received - 06/01/2026',
    date: TODAY, body: 'Your scheduled report is ready. Proxy Material Received, run 06/01/2026 06:00.',
  },
  {
    id: 'pinbox-035', from: 'Report Manager', fromEmail: 'reports@mediantonline.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Report: Proxy Material Report - 06/01/2026',
    date: TODAY, body: 'Your scheduled report is ready. Proxy Material Report, run 06/01/2026 06:00.',
  },
  {
    id: 'pinbox-036', from: 'Unknown Sender', fromEmail: 'info@some-vendor.com',
    to: 'proxymaterial@betanxt.com', cc: [],
    subject: 'Question about proxy timeline',
    date: TODAY, body: 'Wanted to check on the general timeline for this cycle. Not about any specific job.',
  },
  {
    id: 'pinbox-039', from: 'Discovery', fromEmail: 'documents@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Billing verification needed - Beacon Hill Software (Job 9100043)',
    date: TODAY, body: 'Please verify the issuer billing details on this job against what was mailed.', relatedJobId: '9100043',
  },
  {
    id: 'pinbox-041', from: 'Discovery Contest Intake', fromEmail: 'discovery@betanxt.com',
    to: 'ict-contest@betanxt.com', cc: [],
    subject: 'Proxy contest - Hallow Creek Timber Trust (Job 9200006)',
    date: TODAY, body: 'Dissident contest materials received. No solicitor of record identified yet. Please coordinate outreach via the standard contest fallback list.',
    relatedJobId: '9200006',
  },

  // ── Replies to our material follow-ups — classified FOLLOW_UP_REPLY, worked
  // by workflows/followUpReplyWorkflow.ts (ICT-1 / ICT-8 Responses). Each job
  // already has the chase it answers in its followUpHistory. ──
  {
    id: 'pinbox-046', from: 'Georgeson', fromEmail: 'proxy@georgeson.com',
    to: 'ict@betanxt.com', cc: ['proxymaterial@betanxt.com'],
    subject: 'RE: Proxy Material Follow-Up - Elmsworth Transit Authority (Job 9100120)',
    date: TODAY, body: 'Material was shipped via UPS on May 28, tracking 1Z999AA10123456784.', relatedJobId: '9100120',
  },
  {
    id: 'pinbox-047', from: 'Sable Point Resources Investor Relations', fromEmail: 'investor.relations@sablepointresources.com',
    to: 'ict@betanxt.com', cc: ['proxymaterial@betanxt.com'],
    subject: 'RE: Proxy Material Request - Sable Point Resources (Job 9100012)',
    date: TODAY, body: 'We mailed the material to you last Thursday.', relatedJobId: '9100012',
  },
  {
    id: 'pinbox-048', from: 'Alliance Advisors', fromEmail: 'contact@allianceadvisors.com',
    to: 'ict@betanxt.com', cc: ['proxymaterial@betanxt.com'],
    subject: 'RE: Proxy Material Follow-Up - Whitmore Insurance Holdings (Job 9100121)',
    date: TODAY, body: 'The meeting has been postponed. The issuer will confirm new dates.', relatedJobId: '9100121',
  },
  {
    id: 'pinbox-049', from: 'Kingsdale Advisors', fromEmail: 'contact@kingsdaleadvisors.com',
    to: 'ict@betanxt.com', cc: ['proxymaterial@betanxt.com'],
    subject: 'RE: Proxy Material Follow-Up (Canada) - Thunder Bay Minerals Corp (Job 9100062)',
    date: TODAY, body: 'The issuer is a Canadian Issuer and has opted out to distribute to US intermediaries.', relatedJobId: '9100062',
  },
  {
    id: 'pinbox-050', from: 'Laurel Hill', fromEmail: 'assistance@laurelhill.com',
    to: 'ict@betanxt.com', cc: ['proxymaterial@betanxt.com'],
    subject: 'RE: Proxy Material Follow-Up - Brookfield Paper Co (Job 9100014)',
    date: TODAY, body: 'Yes, we are handling the job again this year. Material to follow next week.', relatedJobId: '9100014',
  },
  // ICT-3: the dissident won't send material or pay — Discovery aborts.
  {
    id: 'pinbox-051', from: 'Saratoga Proxy Consulting', fromEmail: 'intake@saratogaproxy.com',
    to: 'ict-contest@betanxt.com', cc: ['proxymaterial@betanxt.com'],
    subject: 'RE: Proxy Contest Outreach - Larkspur Hotels Group (Job 9200007)',
    date: TODAY, body: 'The dissident party will not be sending material to Mediant for distribution and will not pay the contest fee.', relatedJobId: '9200007',
  },

  // ── Delivery failures — classified BOUNCE, surfaced as Proxy Exceptions
  // (see workflows/emailBounceWorkflow.ts) since a bounce means the job's
  // contact on file can't be trusted until someone verifies it. ──
  {
    id: 'pinbox-042', from: 'Mail Delivery System', fromEmail: 'mailer-daemon@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Delivery Status Notification (Failure) - Coastal Marine Terminals (Job 9100021)',
    date: TODAY, body: 'Delivery has failed to these recipients: k.morrow@investor.morrowsodali.com. The address could not be found.',
    relatedJobId: '9100021',
  },
  {
    id: 'pinbox-043', from: 'Mail Delivery System', fromEmail: 'mailer-daemon@betanxt.com',
    to: 'proxymaterial@betanxt.com', cc: [],
    subject: 'Delivery Status Notification (Failure) - Briarwood Insurance Group (Job 9100006)',
    date: TODAY, body: 'Delivery has failed to these recipients: d.ellis@laurelhill.com. The recipient no longer works here.',
    relatedJobId: '9100006',
  },

  // ── More credit holds and bounced chases, so each routing has a handful. ──
  {
    id: 'pinbox-052', from: 'BetaNXT Credit', fromEmail: 'credit@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Credit hold - no fulfillment (Maris-Tech Ltd, Job 3060013)',
    date: TODAY, body: 'Maris-Tech Ltd is on credit hold. Do not fulfill this job until the hold is lifted.',
    relatedJobId: '3060013',
  },
  {
    id: 'pinbox-053', from: 'BetaNXT Credit', fromEmail: 'credit@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Credit hold - no fulfillment (Rayonier Inc, Job 3061540)',
    date: TODAY, body: 'Rayonier Inc is on credit hold. Do not fulfill this job until the hold is lifted.',
    relatedJobId: '3061540',
  },
  {
    id: 'pinbox-054', from: 'Mail Delivery System', fromEmail: 'mailer-daemon@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Delivery Status Notification (Failure) - Talisman Robotics Corp (Job 9100045)',
    date: TODAY, body: 'Delivery has failed to these recipients: m.reyes@allianceadvisors.com. The address could not be found.',
    relatedJobId: '9100045',
  },
  {
    id: 'pinbox-055', from: 'Mail Delivery System', fromEmail: 'mailer-daemon@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Delivery Status Notification (Failure) - Beacon Hill Software (Job 9100043)',
    date: TODAY, body: 'Delivery has failed to these recipients: p.grant@investor.morrowsodali.com. The recipient no longer works here.',
    relatedJobId: '9100043',
  },
  {
    id: 'pinbox-056', from: 'Mail Delivery System', fromEmail: 'mailer-daemon@betanxt.com',
    to: 'ict@betanxt.com', cc: [],
    subject: 'Delivery Status Notification (Failure) - Powell Industries Inc (Job 3021919)',
    date: TODAY, body: 'Delivery has failed to these recipients: s.wu@georgeson.com. Mailbox unavailable.',
    relatedJobId: '3021919',
  },

  // ── Replies to shortage chases (ICT-13), worked as solicitor replies. ──
  {
    id: 'pinbox-057', from: 'Broadridge ICS - Proxy Desk', fromEmail: 'proxy.ops@broadridge.com',
    to: 'ict@betanxt.com', cc: ['proxymaterial@betanxt.com'],
    subject: 'RE: Material shortage - Bellcrest Retail Holdings (Job 4010010)',
    date: TODAY, body: 'We can\'t get more sets to you in time. Please print the 3 shorted sets on demand.', relatedJobId: '4010010',
  },
  {
    id: 'pinbox-058', from: 'Broadridge ICS - Proxy Desk', fromEmail: 'proxy.ops@broadridge.com',
    to: 'ict@betanxt.com', cc: ['proxymaterial@betanxt.com'],
    subject: 'RE: Material shortage - Northstar Agri Corp (Job 4010011)',
    date: TODAY, body: 'The shorted sets aren\'t needed. Those holders will get the material electronically.', relatedJobId: '4010011',
  },
  {
    id: 'pinbox-059', from: 'Broadridge ICS - Proxy Desk', fromEmail: 'proxy.ops@broadridge.com',
    to: 'ict@betanxt.com', cc: ['proxymaterial@betanxt.com'],
    subject: 'RE: Material shortage - Cormorant Energy Partners (Job 4010016)',
    date: TODAY, body: 'We\'ve shipped the 3 short sets today. Tracking 1Z999CE10123456792 (UPS Ground), expected in 2 business days.', relatedJobId: '4010016',
  },
];
